import { c as _c } from "react/compiler-runtime";
import { useOpenCv } from 'opencv-react-ts';
import type { MutableRefObject } from 'react';
import { getLogger, Logger } from '@/idv/utils';
import type { AutoCaptureKind, Resolution, VideoRef } from '../types';
import getSourceDimensions from '../utils/get-source-dimensions';
import { isOpenCvUsable, sharpenImage } from '../utils/graphics-utils/graphics-processing-utils';
type GetImageStringProps = {
  context: CanvasRenderingContext2D;
  videoRef: VideoRef;
  canvasRef: MutableRefObject<HTMLCanvasElement | undefined>;
  videoResolution?: Resolution;
  desiredImageWidth: number;
  desiredImageHeight: number;
  autoCaptureKind: AutoCaptureKind;
  centerOffsetX?: number;
  centerOffsetY?: number;
};
const {
  logWarn
} = getLogger({
  location: 'camera'
});
const useGetImageString = () => {
  const $ = _c(2);
  const {
    cv: openCv
  } = useOpenCv();
  let t0;
  if ($[0] !== openCv) {
    t0 = async t1 => {
      const {
        context,
        videoRef,
        canvasRef,
        videoResolution,
        desiredImageWidth,
        desiredImageHeight,
        autoCaptureKind,
        centerOffsetX: t2,
        centerOffsetY: t3
      } = t1;
      const centerOffsetX = t2 === undefined ? 0 : t2;
      const centerOffsetY = t3 === undefined ? 0 : t3;
      if (!videoRef.current || !canvasRef.current) {
        return null;
      }
      const sourceDimensions = getSourceDimensions({
        videoRef,
        videoResolution,
        desiredImageWidth,
        desiredImageHeight,
        centerOffsetX,
        centerOffsetY
      });
      const {
        sx,
        sy,
        sWidth,
        sHeight
      } = sourceDimensions;
      if (sWidth <= 0 || sHeight <= 0) {
        Logger.error(`Computed desired image dimensions is 0 or negative number - sx: ${sx}, sy: ${sy}, width: ${sWidth}, height: ${sHeight}`);
        return null;
      }
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      canvasRef.current.setAttribute("width", `${sWidth}`);
      canvasRef.current.setAttribute("height", `${sHeight}`);
      context.drawImage(videoRef.current, sx, sy, sWidth, sHeight, 0, 0, canvasRef.current?.clientWidth, canvasRef.current?.clientHeight);
      if (isOpenCvUsable(openCv) && autoCaptureKind !== "face") {
        ;
        try {
          const src = openCv.imread(canvasRef.current);
          const sharpenedImage = sharpenImage(openCv, src, true);
          openCv.imshow(canvasRef.current, sharpenedImage);
          sharpenedImage.delete();
        } catch (t4) {
          const error = t4;
          logWarn("Error sharpening image", error);
        }
      }
      const imageString = canvasRef.current.toDataURL();
      return imageString;
    };
    $[0] = openCv;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const getImageStringFromVideo = t0;
  return getImageStringFromVideo;
};
export default useGetImageString;